import * as THREE from 'three'

interface ModelCenter {
    x: number;
    y: number;
    z: number;
}
interface ModelSize {
    x: number;
    y: number;
    z: number;
}
/**
 * @description 获取模型世界坐标系中心点坐标
 * @doc true
 * @param {THREE.Object3D} model - 模型对象
 * @returns {ModelCenter} 模型世界坐标系中心点坐标
 * ```json
 *  {
 *    "x": 0.000000,
 *    "y": 0.000000,
 *    "z": 0.000000
 *  }
 * ```
 * @example
 * const center = PRL.getModelCenter(model);
 * console.log(center);
 */
export const getModelCenter=(model: THREE.Object3D): ModelCenter=> {
        let worldPosition = new THREE.Vector3();
        model.getWorldPosition(worldPosition);
        
        const box = new THREE.Box3().setFromObject(model);
        const center = new THREE.Vector3();
        box.getCenter(center);
        
        return {
            x: center.x,
            y: center.y,
            z: center.z
        };
}

/**
 * @description 获取模型尺寸
 * @doc true
 * @param {THREE.Object3D} model - 模型对象
 * @returns {ModelSize} 模型尺寸
 * ```json
 *  {
 *    "x": 0.000000,
 *    "y": 0.000000,
 *    "z": 0.000000
 *  }
 * ```
 * @example
 * const size = PRL.getModelSize(model);
 * console.log(size);
 */
export const getModelSize=(model:THREE.Object3D): ModelSize=> {
        const box3 = new THREE.Box3();
        box3.expandByObject(model);
        const v3 = new THREE.Vector3();
        const size = box3.getSize(v3);
        return {
            x: size.x,
            y: size.y,
            z: size.z
        };
}
/**
 * @description 将模型底部平行于地面 并且位于世界中心
 * @doc true
 * @param {THREE.Object3D} model -模型对象
 * @returns {void} 无返回值，直接修改模型位置
 * @example
 * PRL.bottom_parallel_ground(model);
 */
export const bottom_parallel_ground=(model: THREE.Object3D)=> {
        let tmpcenter=getModelCenter(model);
        let tmpsize=getModelSize(model as THREE.Group);
        let CenterOffsetX=0-Number(tmpcenter['x']);
        let CenterOffsetY=0-Number(tmpcenter['y'])+(tmpsize['y']/2);
        let CenterOffsetZ=0-Number(tmpcenter['z']);
        model.position.set(CenterOffsetX,Number(CenterOffsetY),CenterOffsetZ);
        // return model;
}

/**
 * @description 归一化模型尺寸
 * @doc true
 * @param {THREE.Object3D} model - 模型对象
 * @param {number} normalize_size - 目标大小
 * @returns {void} 无返回值，直接修改模型尺寸
 * @example
 * PRL.normalize_model_size(model,100);
 */
 export const normalize_model_size=(model: THREE.Object3D,normalize_size:any)=>{
        let model_size=getModelSize(model );
        let model_size_x=model_size['x'];
        // 计算缩放比例，以x轴为准
        let scaleRatio = Number(normalize_size) / Number(model_size_x);
        model.scale.x *= scaleRatio;
        model.scale.y *= scaleRatio;
        model.scale.z *= scaleRatio;
        model.updateMatrix();
        model.updateMatrixWorld(true);
  }
/**
 * 加载GLTF模型
 * @param url 模型URL
 * @param onLoad 加载完成回调
 * @param onProgress 加载进度回调
 * @param onError 加载错误回调
 * @returns GLTFLoader实例
 */
export const loadGLTFModel = (
  url: string,
  onLoad: (gltf: any) => void,
  onProgress?: (xhr: any) => void,
  onError?: (error: any) => void
) => {
  const { GLTFLoader } = require('three/examples/jsm/loaders/GLTFLoader')
  const loader = new GLTFLoader()
  
  loader.load(url, onLoad, onProgress, onError)
  return loader
}

/**
 * 加载OBJ模型
 * @param url 模型URL
 * @param onLoad 加载完成回调
 * @param onProgress 加载进度回调
 * @param onError 加载错误回调
 * @returns OBJLoader实例
 */
export const loadOBJModel = (
  url: string,
  onLoad: (object: THREE.Object3D) => void,
  onProgress?: (xhr: any) => void,
  onError?: (error: any) => void
) => {
  const { OBJLoader } = require('three/examples/jsm/loaders/OBJLoader')
  const loader = new OBJLoader()
  
  loader.load(url, onLoad, onProgress, onError)
  return loader
}

/**
 * 为模型添加材质
 * @param model 模型对象
 * @param material 材质对象
 */
export const addMaterialToModel = (
  model: THREE.Object3D,
  material: THREE.Material
) => {
  model.traverse((child: any) => {
    if (child.isMesh) {
      child.material = material
    }
  })
}

/**
 * 缩放模型到指定大小
 * @param model 模型对象
 * @param size 目标大小
 */
export const scaleModelToSize = (
  model: THREE.Object3D,
  size: number
) => {
  const box = new THREE.Box3().setFromObject(model)
  const modelSize = box.getSize(new THREE.Vector3()).length()
  const scaleFactor = size / modelSize
  
  model.scale.set(scaleFactor, scaleFactor, scaleFactor)
}

/**
 * 居中模型
 * @param model 模型对象
 */
export const centerModel = (model: THREE.Object3D) => {
  const box = new THREE.Box3().setFromObject(model)
  const center = box.getCenter(new THREE.Vector3())
  
  model.position.sub(center)
}

/**
 * 旋转模型
 * @param model 模型对象
 * @param x X轴旋转角度（弧度）
 * @param y Y轴旋转角度（弧度）
 * @param z Z轴旋转角度（弧度）
 */
export const rotateModel = (
  model: THREE.Object3D,
  x: number,
  y: number,
  z: number
) => {
  model.rotation.set(x, y, z)
}

/**
 * 移动模型
 * @param model 模型对象
 * @param x X轴位置
 * @param y Y轴位置
 * @param z Z轴位置
 */
export const moveModel = (
  model: THREE.Object3D,
  x: number,
  y: number,
  z: number
) => {
  model.position.set(x, y, z)
}

/**
 * 克隆模型
 * @param model 模型对象
 * @returns 克隆后的模型
 */
export const cloneModel = (model: THREE.Object3D) => {
  return model.clone()
}

/**
 * 计算模型边界盒
 * @param model 模型对象
 * @returns 边界盒
 */
export const calculateModelBoundingBox = (model: THREE.Object3D) => {
  return new THREE.Box3().setFromObject(model)
}

/**
 * 计算模型中心点
 * @param model 模型对象
 * @returns 中心点向量
 */
export const calculateModelCenter = (model: THREE.Object3D) => {
  const box = new THREE.Box3().setFromObject(model)
  return box.getCenter(new THREE.Vector3())
}